<template>
    <div class="space-y-6">
        <div class="card bg-base-100 shadow-lg border border-base-200">
            <div class="card-body">
                <h2 class="card-title text-lg font-semibold mb-4 flex items-center">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M3 5h12M9 3v2m3 6h9m-3-2v2m-9 6h12m-3-2v2m-9 6h9m-3-2v2M3 10h6v4H3v-4zm0 10h6v-4H3v4z" />
                    </svg>
                    {{ $tt('language.setting.title') }}
                </h2>

                <div class="form-control w-full max-w-md">
                    <label class="label">
                        <span class="label-text font-medium">{{ $tt('language.setting.label') }}</span>
                    </label>
                    <select class="select select-bordered focus:select-primary" v-model="currentLocale">
                        <option v-for="item in languageOptions" :key="item.value" :value="item.value">
                            {{ item.label }}
                        </option>
                    </select>
                    <label class="label">
                        <span class="label-text-alt text-base-content/70">{{ $tt('language.setting.help') }}</span>
                    </label>
                </div>
            </div>
        </div>

        <!-- 网站标题设置 -->
        <div class="card bg-base-100 shadow-lg border border-base-200">
            <div class="card-body">
                <h2 class="card-title text-lg font-semibold mb-4 flex items-center">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z">
                        </path>
                    </svg>
                    {{ $tt('personalization.site.titleSection') }}
                </h2>

                <div class="space-y-4">
                    <div class="form-control w-full max-w-md">
                        <label class="label">
                            <span class="label-text font-medium">{{ $tt('personalization.site.titleLabel') }}</span>
                        </label>
                        <input type="text" :placeholder="$tt('personalization.site.titlePlaceholder')" v-model="formData.site_title"
                            @change="updateSys('site_title')" class="input input-bordered focus:input-primary" />
                        <label class="label">
                            <span class="label-text-alt text-base-content/70">{{ $tt('personalization.site.titleHelp') }}</span>
                        </label>
                    </div>

                    <div class="form-control">
                        <label class="label cursor-pointer justify-start gap-4">
                            <span class="label-text font-medium">{{ $tt('personalization.site.showRoleBadge') }}</span>
                            <input type="checkbox" class="toggle toggle-primary toggle-lg"
                                v-model="formData.show_role_badge" @change="updateSys('show_role_badge')" />
                        </label>
                        <label class="label">
                            <span class="label-text-alt text-base-content/70">{{ $tt('personalization.site.showRoleBadgeHelp') }}</span>
                        </label>
                    </div>
                </div>
            </div>
        </div>

        <div class="card bg-base-100 shadow-lg border border-base-200">
            <div class="card-body">
                <h2 class="card-title text-lg font-semibold mb-4 flex items-center">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M12 6v6l4 2M19 6a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    {{ $tt('personalization.theme.title') }}
                </h2>
                <div class="text-sm text-base-content/70 mb-4">{{ $tt('personalization.theme.current', { theme: currentTheme }) }}</div>
                <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    <div v-for="theme in themes" :key="theme"
                        class="border-base-content/20 hover:border-base-content/40 overflow-hidden rounded-lg border outline outline-2 outline-offset-2 transition"
                        :class="currentTheme === theme ? '!outline-base-content' : 'outline-transparent'"
                        :data-set-theme="theme" data-act-class="!outline-base-content" @click="switchTheme(theme)">
                        <div :data-theme="theme" class="bg-base-100 text-base-content w-full cursor-pointer font-sans">
                            <div class="grid grid-cols-5 grid-rows-3">
                                <div class="bg-base-200 col-start-1 row-span-2 row-start-1"></div>
                                <div class="bg-base-300 col-start-1 row-start-3"></div>
                                <div
                                    class="bg-base-100 col-span-4 col-start-2 row-span-3 row-start-1 flex flex-col gap-1 p-2">
                                    <div class="font-bold">{{ theme }}</div>
                                    <div class="flex flex-wrap gap-1">
                                        <div class="bg-primary flex aspect-square w-5 items-center justify-center rounded lg:w-6">
                                            <div class="text-primary-content text-sm font-bold">A</div>
                                        </div>
                                        <div class="bg-secondary flex aspect-square w-5 items-center justify-center rounded lg:w-6">
                                            <div class="text-secondary-content text-sm font-bold">A</div>
                                        </div>
                                        <div class="bg-accent flex aspect-square w-5 items-center justify-center rounded lg:w-6">
                                            <div class="text-accent-content text-sm font-bold">A</div>
                                        </div>
                                        <div class="bg-neutral flex aspect-square w-5 items-center justify-center rounded lg:w-6">
                                            <div class="text-neutral-content text-sm font-bold">A</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="card bg-base-100 shadow-lg border border-base-200">
            <div class="card-body">
                <h2 class="card-title text-lg font-semibold mb-4 flex items-center">
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M3 5a2 2 0 012-2h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5zm3 12l3-4 2 3 3-4 4 5" />
                    </svg>
                    {{ $tt('personalization.background.title') }}
                </h2>

                <div class="grid grid-cols-1 gap-4">
                    <div>
                        <div class="text-sm font-medium mb-2">{{ $tt('personalization.background.hide') }}</div>
                        <input type="checkbox" class="toggle" v-model="isHiddenBGP" />
                    </div>
                    <div>
                        <div class="text-sm font-medium mb-2">{{ $tt('personalization.background.custom') }}</div>
                        <div class="flex flex-col gap-2">
                            <div class="flex items-center gap-2 flex-wrap">
                                <input type="file" class="file-input file-input-sm file-input-bordered w-full max-w-xs"
                                    accept="image/*" @change="handleBackgroundUpload" ref="backgroundInput" />
                                <button class="btn btn-sm" @click="clearBackground">{{ $tt('personalization.background.clear') }}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { THEME_LIST, THEME_COLOR_MAP } from '@/utils/themeConfig'
import { initTheme, switchTheme } from '@/utils/themeUtils'

export default {
    name: 'PersonalizationSettings',
    props: {
        formData: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            currentTheme: 'light',
            themes: THEME_LIST,
            colorMap: THEME_COLOR_MAP,
        }
    },
    computed: {
        ...mapGetters(['getUsername', 'getIsSuperuser']),
        languageOptions() {
            return [
                { value: 'zh-CN', label: this.$tt('language.option.zhCN') },
                { value: 'zh-TW', label: this.$tt('language.option.zhTW') },
                { value: 'en-US', label: this.$tt('language.option.enUS') }
            ]
        },
        currentLocale: {
            get() {
                return this.$localeState.locale
            },
            set(locale) {
                this.handleLanguageChange(locale)
            }
        },
        isHiddenBGP: {
            get() {
                return window.localStorage.getItem('isHiddenBGP') === 'true'
            },
            set(value) {
                window.localStorage.setItem('isHiddenBGP', value ? 'true' : 'false')
            }
        }
    },
    created() {
        this.initTheme()
    },
    methods: {
        updateSys(key) {
            this.$emit('update-sys', key)
        },
        handleLanguageChange(locale) {
            const targetLocale = this.$setLocale(locale)
            const localeName = this.languageOptions.find(item => item.value === targetLocale)?.label || targetLocale
            this.$myMsg.notify({
                title: this.$tt('language.change.successTitle'),
                content: this.$tt('language.change.successContent', { name: localeName }),
                type: 'success',
                time: 1500,
            })
            this.$root.$emit('refresh-page')
        },
        initTheme() {
            this.currentTheme = initTheme(this.currentTheme, this.colorMap)
        },
        switchTheme(theme) {
            this.currentTheme = switchTheme(theme, this.colorMap)
        },
        handleBackgroundUpload(event) {
            const file = event.target.files[0]
            if (file) {
                const formData = new FormData()
                formData.append('upload_file', file)

                this.$api.Task.uploadImageAndSave(formData).then((res) => {
                    if (res.result) {
                        this.$myMsg.notify({
                            title: this.$tt('personalization.upload.successTitle'),
                            content: this.$tt('personalization.upload.successContent'),
                            type: 'success',
                            time: 3000,
                        })
                    } else {
                        this.$myMsg.notify({
                            title: this.$tt('personalization.upload.failTitle'),
                            content: res.message,
                            type: 'error',
                            time: 3000,
                        })
                    }
                }).catch(error => {
                    this.$myMsg.notify({
                        title: this.$tt('personalization.upload.failTitle'),
                        content: error.message,
                        type: 'error',
                        time: 3000,
                    })
                })
            }
        },
        clearBackground() {
            this.$api.Task.restoreLogo().then((res) => {
                if (res.result) {
                    this.$myMsg.notify({
                        title: this.$tt('personalization.restore.successTitle'),
                        content: this.$tt('personalization.restore.successContent'),
                        type: 'success',
                        time: 3000,
                    })
                }
            })
        }
    }
}
</script>
